import React, { useEffect, useLayoutEffect, useState, useRef, useCallback, useMemo } from 'react';
import ReactDOM from 'react-dom';
import {
  normalizeAccreditationPhotoForFormio,
  getAccreditationPhotoDisplayUrls,
} from '../../utils/emsPhotoUtils';
import { ACCREDITATION_ALLOWED_FILE_TYPES } from '../../constants/accreditationUploads';
import AccreditationPhotoManager from '../user/AccreditationPhotoManager';
import { useParams, useLocation } from 'react-router-dom';
import '../../components/css/FormioOverrides.css';
import '../css/UserFormHeader.css';
import FormioTabRenderer from './FormioTabRenderer';
import { GoldLoader } from '../shared/Loaders';
import ParticipantDetailsSidebar, {
  resolveMissionDates,
  resolveParticipantDatePeriods,
} from './ParticipantDetailsSidebar';
/* After FormioTabRenderer so Personal Settings radio layout beats UserFormView :has() */
import '../css/ParticipantEdit.css';
/* After Validation so Personal Settings layout wins cascade */
import '../css/ParticipantEditValidation.css';
import '../css/ParticipantEditPersonalSettings.css';
import '../css/ParticipantDetailsSidebar.css';
import { forceFormioShowValidationErrors } from '../../utils/formioSubmissionNormalize';
import {
  formSchemaHasCaddieSelect,
  normalizeDataGridForFormioRender,
  copyCaddieFieldsFromSource,
  personDataHasCaddieFields,
  preserveAndNormalizeCaddiesOnSave,
} from '../../utils/caddieFormioUtils';
import { getAddCaddieConfig } from '../../utils/addCaddieOption';
import {
  buildInitialFormData,
  filterPersonDataForFormSave,
  preserveParticipantIdentityFromPerson,
  collectFormDataKeys,
  applyScoreboardNameFallback,
} from '../../utils/formSchemaUtils';
import { isInvitationTypesFormName, formatAccreditationCategoryLabel } from '../../utils/invitationTypeFormioUtils';
import {
  isAthleteOrCaddieGroup,
  syncAssignmentsIntoForm,
  syncInvitationTypeIntoForm,
} from '../../utils/participantAssignmentUtils';
import { normalizePersonId } from '../../utils/personId';
import axios from 'axios';
import axiosAuth from '../../api/axiosInstance';
import mergeWith from 'lodash/mergeWith';
import cloneDeep from 'lodash/cloneDeep';
import AccommodationSection from './AccommodationSection';
import ParticipantBookingTab from './ParticipantBookingTab';
import ParticipantEmailHistory from './ParticipantEmailHistory';
import SendResultModal from './SendResultModal';
import { Spinner } from 'react-bootstrap';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPen, faUser, faChevronLeft, faChevronRight } from '@fortawesome/free-solid-svg-icons';
import { resolvePersonImageUrl } from '../../utils/resolvePersonImageUrl';
import { calculateVisibleFieldProgress } from '../../utils/formVisibleProgress';
import { tabPercentFromProgress } from '../user/UserFormPrototypeHeader';
import { laDateKeyToUTC } from '../../utils/laDateUtils';
import { dropTrailingBlankAccommodationRows } from '../../utils/accommodationRows';
import { dropTrailingBlankTravelRows } from '../../utils/travelRows';
import { filterSendableTemplates } from '../../utils/sendableTemplates';
import useForwardWheelScroll from '../../hooks/useForwardWheelScroll';

const TABS = ['REGISTRATION', 'ACCREDITATION', 'TRAVEL', 'ACCOMMODATION', 'GUEST', 'PERSONAL SETTINGS', 'SEND EMAIL', 'BOOKING'];

/** Display label — sentence case like Participant Edit Prototype (REGISTRATION → Registration). */
const tabDisplayLabel = (tab) =>
  tab ? tab.charAt(0) + tab.slice(1).toLowerCase() : '';

/** Map PE tab → progress_breakdown segment (same keys as user portal where possible). */
const TAB_PROGRESS_KEY = {
  REGISTRATION: 'registration',
  ACCREDITATION: 'accreditation',
  TRAVEL: 'travel',
  ACCOMMODATION: 'accommodation',
  GUEST: 'guest',
  'PERSONAL SETTINGS': 'personalSettings',
};

const participantFormTabClass = (tab) =>
  `participant-form-tab participant-form-tab-${String(tab).replace(/\s+/g, '-')}`;
/** Tabs without a Form.io form (no save on switch, no Submit button). */
const NO_FORM_TABS = ['SEND EMAIL', 'BOOKING'];
const TABS_REQUIRING_DATA = TABS.filter((t) => !NO_FORM_TABS.includes(t));
/** Tabs hidden entirely for Athletes/Caddies (accreditation, hotel booking, travel,
 * accommodation, and personal settings are handled outside EMS for this participant
 * type — IGFEMS-564). */
const HIDDEN_TABS_FOR_ATHLETE_CADDIE = ['ACCREDITATION', 'BOOKING', 'TRAVEL', 'ACCOMMODATION', 'PERSONAL SETTINGS'];

/** Same form IDs as UserFormView — one form per tab (do not map by API list index). */
const FORM_TAB_IDS = {
  REGISTRATION: '68c7eefbd44999eedb541043',
  ACCREDITATION: '68e6505866f25479291b933f',
  TRAVEL: '68e781408e1384b2c283c3af',
  ACCOMMODATION: '68e784018e1384b2c283c3bb',
  GUEST: '68e785cb8e1384b2c283c3c5',
};

/** Participant-edit profile upload endpoint (EMS uploads; independent from accreditation form photo URL) */
const getParticipantEditUploadUrl = (API_URL) => {
  const base = (API_URL || '').replace(/\/$/, '');
  return base ? `${base}/ems/uploads` : '/api/ems/uploads';
};

/**
 * Name of the selected role / invitation type / accreditation for Form.io conditions.
 * Falls back to the populated doc on the participant, so the name is right even before
 * the dropdown list loads or when the item belongs to another competition.
 */
const assignmentName = (list, selectedId, populated, nameField) => {
  if (!selectedId) return '';
  const doc =
    list.find((item) => String(item._id) === String(selectedId)) ||
    (populated && typeof populated === 'object' && String(populated._id) === String(selectedId)
      ? populated
      : null);
  return doc?.[nameField] || '';
};

const ParticipantEdit = () => {
  const { id } = useParams();
  const location = useLocation();
  const API_URL = process.env.REACT_APP_API_URL || '';

  const getInitialTab = () => {
    const params = new URLSearchParams(location.search);
    const tabParam = (params.get('tab') || '').toLowerCase();
    if (tabParam === 'email') return 'SEND EMAIL';
    // Permissions menu → Personal settings (invitation types) form
    if (tabParam === 'personal-settings' || tabParam === 'permissions' || tabParam === 'personal') {
      return 'PERSONAL SETTINGS';
    }
    // Accommodation menu → Accommodation form
    if (tabParam === 'accommodation' || tabParam === 'accomodation') {
      return 'ACCOMMODATION';
    }
    return TABS[0];
  };

  const [participant, setParticipant] = useState(null);
  /** Stable tab → formId map (PERSONAL SETTINGS filled in after name resolve). */
  const [forms, setForms] = useState(() => ({ ...FORM_TAB_IDS }));
  const [personalSettingsResolving, setPersonalSettingsResolving] = useState(true);
  /** Bumps when a form schema is cached — forces FormioTabRenderer to see initialSchema. */
  const [schemaCacheTick, setSchemaCacheTick] = useState(0);
  const schemaCacheRef = useRef({});
  const [loading, setLoading] = useState(true);
  const hasLoadedOnce = useRef(false);
  /** Prevents overlapping tab-switch background saves. */
  const tabSwitchSaveInFlightRef = useRef(false);
  const [activeTab, setActiveTab] = useState(getInitialTab);
  const [templates, setTemplates] = useState([]);
  const [selectedTemplateId, setSelectedTemplateId] = useState('');
  const [registrationStatus, setRegistrationStatus] = useState('');
  const [approvalStatus, setApprovalStatus] = useState('Pending');
  const [groups, setGroups] = useState([]);
  // "+ Add as a new caddie" in the Caddie Name dropdown needs the tournament's Caddie group (IGFEMS-577).
  const addCaddieConfig = useMemo(() => getAddCaddieConfig(groups), [groups]);
  const [selectedGroup, setSelectedGroup] = useState('');
  const [invitationTypes, setInvitationTypes] = useState([]);
  const [selectedInvitationType, setSelectedInvitationType] = useState('');
  const [emsRoles, setEmsRoles] = useState([]);
  const [selectedEmsRole, setSelectedEmsRole] = useState('');
  const [accreditationCategories, setAccreditationCategories] = useState([]);
  const [selectedAccreditationCategory, setSelectedAccreditationCategory] = useState('');
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [assignmentSaving, setAssignmentSaving] = useState(false);
  const [assignmentSaveError, setAssignmentSaveError] = useState('');
  const [profilePhoto, setProfilePhoto] = useState('');
  const [bookingGroups, setBookingGroups] = useState([]);
  /** Tab completion % — same shape as user portal progress_breakdown. */
  const [tabProgress, setTabProgress] = useState({});
  const tabsTrackRef = useRef(null);
  const tabSliderRef = useRef(null);
  const tabBtnRefs = useRef({});
  const fileInputRef = useRef(null);
  /** Latest Form.io submission.data for the active tab (may be ahead of React state when onChange skips parent). */
  const latestFormDataRef = useRef(null);
  /** Active tab Form.io schema components — used to filter person_data on save. */
  const activeFormSchemaRef = useRef(null);
  /** Set by FormioTabRenderer — call before save to snapshot live Webform data. */
  const flushLiveFormDataRef = useRef(() => {});
  /** Live Form.io webform — used to read default caddie radio on save. */
  const formioWebformRef = useRef(null);
  /** Set by FormioTabRenderer — the open form, for pushing Summary changes into it. */
  const getLiveWebformRef = useRef(() => null);
  /** CRM caddie lookup — synced from FormioTabRenderer when registration form loads. */
  const caddiesByIdRef = useRef(new Map());
  const hasRegistrationCaddieRef = useRef(false);
  /** Tab switch: quiet save (no full validation). */
  const triggerSaveRef = useRef(() => Promise.resolve(true));
  /** Footer Submit: formio.submit() with validation. */
  const triggerFormSubmitRef = useRef(() => Promise.resolve());
  /** Set by FormioTabRenderer — clears Form.io error panels after successful save. */
  const clearFormErrorsRef = useRef(() => {});
  /** Prevents handleFormSave firing multiple times for a single submit (Form.io fires onSubmit per panel). */
  const isSavingRef = useRef(false);
  const pageMessageTimerRef = useRef(null);
  /** Tab label at save time (avoids wrong message if activeTab changes before async save finishes). */
  const activeTabRef = useRef(activeTab);

  /** Display name only — SMTP auth always uses SMTP_USER / SMTP_PASS from server .env */
  const [fromName] = useState('IGF Team');

  const quillRef = useRef(null);
  const [editorHtml, setEditorHtml] = useState('');
  const [templateName, setTemplateName] = useState('');
  const [templateSubject, setTemplateSubject] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [sendResult, setSendResult] = useState(null);
  const [templateFiles, setTemplateFiles] = useState([]);
  const [placeholders] = useState(['{{name}}', '{{email}}', '{{phone}}', '{{competition}}']);
  const [submissionVersion, setSubmissionVersion] = useState(0);
  /** Incremented after a bust-cache fetch on the Accreditation tab; forces FormioTabRenderer remount. */
  const [accreditationKey, setAccreditationKey] = useState(0);
  /** Ref forwarded to AccreditationPhotoManager so we can call openCrop/openView from the portal button. */
  const photoManagerRef = useRef(null);
  /** DOM node of the Formio photo field container — used as portal target. */
  const [photoFieldPortalEl, setPhotoFieldPortalEl] = useState(null);
  const tabContentRef = useRef(null);
  /** The .participant-edit-scroll element that actually scrolls the tab body. */
  const scrollContainerRef = useRef(null);
  /** Whole participant page area (not the app sidebar) — wheel here scrolls the tab body. */
  const pageRef = useRef(null);
  useForwardWheelScroll(pageRef, scrollContainerRef);
  /** Same pattern as UserLogin: { type, text } or failed save validation { type, text, validationItems } */
  const [pageMessage, setPageMessage] = useState(null);
  /** Reveal Form.io required-field highlights after failed Save (same as user portal). */
  const [showInlineErrors, setShowInlineErrors] = useState(false);

  const isAthleteOrCaddie = useMemo(
    () =>
      isAthleteOrCaddieGroup({
        participantGroup: participant?.group_id,
        groups,
        selectedGroupId: selectedGroup,
      }),
    [participant?.group_id, groups, selectedGroup]
  );

  const visibleTabs = useMemo(
    () =>
      TABS.filter(
        (tab) => !isAthleteOrCaddie || !HIDDEN_TABS_FOR_ATHLETE_CADDIE.includes(tab)
      ),
    [isAthleteOrCaddie]
  );

  useEffect(() => {
    if (isAthleteOrCaddie && HIDDEN_TABS_FOR_ATHLETE_CADDIE.includes(activeTab)) {
      setActiveTab(TABS[0]);
    }
  }, [isAthleteOrCaddie, activeTab]);

  useEffect(() => {
    activeTabRef.current = activeTab;
  }, [activeTab]);

  /** The scroll container stays mounted across tabs, so start each new tab at the top. */
  useLayoutEffect(() => {
    if (scrollContainerRef.current) scrollContainerRef.current.scrollTop = 0;
  }, [activeTab]);

  /** Role / invitation type / accreditation names injected into form data for Form.io conditions. */
  const roleName = assignmentName(emsRoles, selectedEmsRole, participant?.role_id, 'role_name');
  const invitationTypeName = assignmentName(invitationTypes, selectedInvitationType, participant?.invitation_type_id, 'name');
  const accreditationName = assignmentName(
    accreditationCategories, selectedAccreditationCategory, participant?.accreditation_category_id, 'category_name'
  );
  const assignmentNames = useMemo(
    () => ({ roleName, invitationTypeName, accreditationName }),
    [roleName, invitationTypeName, accreditationName]
  );

  const selectedGroupName = groups.find((g) => String(g._id) === String(selectedGroup))?.group_name;

  /** Keep the open form in step with Summary changes. */
  useEffect(() => {
    syncAssignmentsIntoForm(getLiveWebformRef.current(), latestFormDataRef.current, {
      ...assignmentNames,
      ...(selectedGroup ? { Group_id: selectedGroup } : {}),
      ...(selectedGroupName ? { groupName: selectedGroupName } : {}),
    });
  }, [assignmentNames, selectedGroup, selectedGroupName]);

  /** Separate effect: a Role change must not overwrite an unsaved pick in the form's own dropdown. */
  useEffect(() => {
    syncInvitationTypeIntoForm(getLiveWebformRef.current(), latestFormDataRef.current, selectedInvitationType);
  }, [selectedInvitationType]);

  /* --------------------- Utilities --------------------- */
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'N/A';
    
    const day = String(date.getDate()).padStart(2, '0');
    const month = new Intl.DateTimeFormat('en-GB', { month: 'short' }).format(date);
    const year = date.getFullYear();
    
    return `${day}-${month}-${year}`;
  };

  /* --------------------- Fetch templates --------------------- */
  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        const res = await axios.get(`${API_URL}/ems/templates`);
        setTemplates(filterSendableTemplates(res.data));
      } catch (err) {
        console.error('Failed to fetch templates', err);
      }
    };
    fetchTemplates();
  }, [API_URL]);

  /* --------------------- Fetch groups --------------------- */
  useEffect(() => {
    const fetchGroups = async () => {
      try {
        const res = await axios.get(`${API_URL}/ems/groups`);
        const data = Array.isArray(res.data) ? res.data : res.data?.data || [];
        setGroups(data);
      } catch (err) {
        console.error('Failed to fetch groups', err);
      }
    };
    fetchGroups();
  }, [API_URL]);

  /* --------------------- Fetch invitation types --------------------- */
  useEffect(() => {
    const fetchInvitationTypes = async () => {
      try {
        const res = await axios.get(`${API_URL}/ems/invitation-types?all=true`);
        setInvitationTypes(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Failed to fetch invitation types', err);
      }
    };
    fetchInvitationTypes();
  }, [API_URL]);

  /* --------------------- Fetch EMS roles --------------------- */
  useEffect(() => {
    const fetchEmsRoles = async () => {
      try {
        const res = await axios.get(`${API_URL}/ems/roles`);
        setEmsRoles(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Failed to fetch EMS roles', err);
      }
    };
    fetchEmsRoles();
  }, [API_URL]);

  /* --------------------- Fetch accreditation categories --------------------- */
  useEffect(() => {
    const fetchAccreditationCategories = async () => {
      try {
        const res = await axios.get(`${API_URL}/ems/accreditation-categories`);
        setAccreditationCategories(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Failed to fetch accreditation categories', err);
      }
    };
    fetchAccreditationCategories();
  }, [API_URL]);

  /* Invitation Types Form has no stable ID — resolve by name; never wipe FORM_TAB_IDS. */
  useEffect(() => {
    let cancelled = false;
    const resolveInvitationTypesForm = async () => {
      setPersonalSettingsResolving(true);
      try {
        const res = await axios.get(`${API_URL}/ems/forms`);
        if (cancelled) return;
        const list = Array.isArray(res.data) ? res.data : res.data?.data || [];
        const match = list.find((f) => isInvitationTypesFormName(f?.form_name));
        if (match?._id) {
          setForms((prev) => ({ ...prev, 'PERSONAL SETTINGS': String(match._id) }));
        }
      } catch (err) {
        console.error('Failed to resolve Invitation Types form', err);
      } finally {
        if (!cancelled) setPersonalSettingsResolving(false);
      }
    };
    resolveInvitationTypesForm();
    return () => { cancelled = true; };
  }, [API_URL]);

  /* Prefetch every tab schema once — tab switches must not wait on network. */
  useEffect(() => {
    if (!participant?._id || !API_URL) return;
    let cancelled = false;
    const ids = [...new Set(Object.values(forms).filter(Boolean))];
    ids.forEach(async (formId) => {
      if (schemaCacheRef.current[formId]?.schema?.components) return;
      try {
        const res = await axios.get(`${API_URL}/ems/forms/${formId}`);
        if (cancelled || !res.data?.schema?.components) return;
        schemaCacheRef.current[formId] = {
          schema: res.data.schema,
          form_name: res.data.form_name || '',
        };
        setSchemaCacheTick((t) => t + 1);
      } catch (err) {
        console.warn('Prefetch form schema failed:', formId, err?.message || err);
      }
    });
    return () => { cancelled = true; };
  }, [participant?._id, forms, API_URL]);

  /**
   * Recompute ALL tab % from person_data + cached schemas (same math as user portal).
   * Skips the active tab when live Form.io already reported progress (avoids stomping mid-edit).
   */
  useEffect(() => {
    if (!participant?._id || !schemaCacheTick) return;
    const personData = safeParsePersonData(participant.person_data);
    const activeSegment = TAB_PROGRESS_KEY[activeTab];
    setTabProgress((prev) => {
      const next = { ...prev };
      let changed = false;
      Object.entries(TAB_PROGRESS_KEY).forEach(([tab, segment]) => {
        const formId = forms[tab];
        if (!formId) return;
        const components = schemaCacheRef.current[formId]?.schema?.components;
        if (!Array.isArray(components) || !components.length) return;
        /* Keep live active-tab stats when Form.io already counted fields. */
        if (
          segment === activeSegment &&
          prev[segment] &&
          Number(prev[segment].total) > 0
        ) {
          return;
        }
        const { filled, total } = calculateVisibleFieldProgress(
          personData,
          components,
          null
        );
        const prevSeg = prev[segment];
        if (
          Number(prevSeg?.total) === total &&
          Number(prevSeg?.completed) === filled
        ) {
          return;
        }
        next[segment] = {
          total,
          completed: filled,
          display: `${filled}/${total}`,
        };
        changed = true;
      });
      return changed ? next : prev;
    });
    // activeTab intentionally omitted — only recompute when data/schemas change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [participant?._id, participant?.person_data, forms, schemaCacheTick]);

  /* --------------------- Fetch Accommodation Bookings --------------------- */
  useEffect(() => {
    const personOid = participant?.person_id?._id;
    if (activeTab !== 'ACCOMMODATION' || !personOid) return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(
          `${API_URL}/ems/room-bookings/grouped?participant_id=${personOid}`
        );
        if (!cancelled) setBookingGroups(Array.isArray(res.data) ? res.data : []);
      } catch (err) {
        console.error('Error fetching accommodation bookings:', err);
        if (!cancelled) setBookingGroups([]);
      }
    })();
    return () => { cancelled = true; };
  }, [activeTab, participant?.person_id?._id, API_URL]);

  /* --------------------- Fetch participant (shared so we can refetch after save) --------------------- */
  const fetchParticipantData = React.useCallback(async (bustCache = false) => {
    if (!id) return;
    const requestId = id;
    // Only show the full-page loading overlay on the very first load.
    // Subsequent background fetches (e.g. after save) must not unmount FormioTabRenderer.
    if (!hasLoadedOnce.current) setLoading(true);
    try {
      const url = bustCache
        ? `${API_URL}/ems/participants/${requestId}?_=${Date.now()}`
        : `${API_URL}/ems/participants/${requestId}`;
      const res = await axios.get(url);
      if (requestId !== id) return;

      const p = res.data;
      const cleanData = safeParsePersonData(p?.person_data);

      setParticipant(p || null);
      if (p?.progress_breakdown && typeof p.progress_breakdown === 'object') {
        setTabProgress((prev) => {
          const next = { ...prev };
          Object.entries(p.progress_breakdown).forEach(([segment, db]) => {
            if (!db || typeof db !== 'object') return;
            const local = prev[segment];
            /* Prefer fresher local counts when DB is still 0/stale (same as PanelDataService). */
            if (
              local &&
              Number(local.total) > 0 &&
              Number(local.completed) > Number(db.completed || 0)
            ) {
              return;
            }
            next[segment] = {
              total: Number(db.total) || 0,
              completed: Number(db.completed) || 0,
            };
          });
          return next;
        });
      }

      // profilePhoto tracks only the separate "editProfilePhoto" upload field (header button).
      const editPhoto = cleanData.editProfilePhoto;
      const editRef = Array.isArray(editPhoto) && editPhoto[0]
        ? (typeof editPhoto[0] === 'object' ? (editPhoto[0].url ?? '') : editPhoto[0])
        : (editPhoto && typeof editPhoto !== 'object' ? editPhoto : '');
      setProfilePhoto(String(editRef || ''));
      setRegistrationStatus(p?.registration_status || '');
      setApprovalStatus(p?.approval_status || 'Pending');

      const groupVal = p?.group_id
        ? typeof p.group_id === 'object'
          ? String(p.group_id._id || '')
          : String(p.group_id)
        : '';
      setSelectedGroup(groupVal);
      const invitationVal = p?.invitation_type_id
        ? typeof p.invitation_type_id === 'object'
          ? String(p.invitation_type_id._id || '')
          : String(p.invitation_type_id)
        : '';
      setSelectedInvitationType(invitationVal);
      if (p?.invitation_type_id && typeof p.invitation_type_id === 'object' && p.invitation_type_id._id) {
        setInvitationTypes(prev => {
          const id = String(p.invitation_type_id._id);
          if (prev.some(t => String(t._id) === id)) return prev;
          return [...prev, p.invitation_type_id];
        });
      }
      const roleVal = p?.role_id
        ? typeof p.role_id === 'object'
          ? String(p.role_id._id || '')
          : String(p.role_id)
        : '';
      setSelectedEmsRole(roleVal);
      const accreditationVal = p?.accreditation_category_id
        ? typeof p.accreditation_category_id === 'object'
          ? String(p.accreditation_category_id._id || '')
          : String(p.accreditation_category_id)
        : '';
      setSelectedAccreditationCategory(accreditationVal);
      latestFormDataRef.current = personDataHasCaddieFields(cleanData)
        ? normalizeDataGridForFormioRender(JSON.parse(JSON.stringify(cleanData)))
        : cleanData;
      // Do NOT bump submissionVersion here — every bump remounts Form.io (skeleton flash / blank form).
      // Callers that need a remount (save, photo crop) bump explicitly.
    } catch (err) {
      console.error('Error fetching participant data:', err);
    } finally {
      if (requestId === id) {
        hasLoadedOnce.current = true;
        setLoading(false);
      }
    }
  }, [id, API_URL]);

  useEffect(() => {
    if (id) fetchParticipantData();
  }, [id, fetchParticipantData]);

  /* The tab strip is one scrolling row; show an arrow on whichever side still has hidden tabs. */
  const [tabScroll, setTabScroll] = useState({ left: false, right: false });
  const updateTabScroll = useCallback(() => {
    const track = tabsTrackRef.current;
    if (!track) return;
    const left = track.scrollLeft > 1;
    const right = track.scrollLeft + track.clientWidth < track.scrollWidth - 1;
    setTabScroll((prev) => (prev.left === left && prev.right === right ? prev : { left, right }));
  }, []);
  const scrollTabs = (direction) => {
    const track = tabsTrackRef.current;
    if (track) track.scrollBy({ left: direction * track.clientWidth * 0.6, behavior: 'smooth' });
  };

  /* Prototype tab slider — mutate DOM left/width (exact HTML prototype), not React state.
   * Must re-run after loading ends (tabs mount) or pill stays at 0 and labels go white-on-white. */
  const positionTabSlider = useCallback(() => {
    const track = tabsTrackRef.current;
    const slider = tabSliderRef.current;
    const btn = tabBtnRefs.current[activeTab];
    if (!track || !slider || !btn) return;
    const trackRect = track.getBoundingClientRect();
    const btnRect = btn.getBoundingClientRect();
    if (btnRect.width === 0) {
      window.setTimeout(positionTabSlider, 50);
      return;
    }
    const firstPaint = !track.hasAttribute('data-slider-ready');
    if (firstPaint) slider.style.transition = 'none';
    /* The slider lives inside the scrolling track, so it moves with the chips: position it in
     * content coordinates (viewport offset + scrollLeft). */
    slider.style.top = `${btnRect.top - trackRect.top}px`;
    slider.style.height = `${btnRect.height}px`;
    slider.style.left = `${btnRect.left - trackRect.left + track.scrollLeft}px`;
    slider.style.width = `${btnRect.width}px`;
    if (firstPaint) {
      void slider.offsetWidth;
      slider.style.transition = '';
      track.setAttribute('data-slider-ready', 'true');
    }
  }, [activeTab]);

  useLayoutEffect(() => {
    if (loading || !participant) return undefined;
    let cancelled = false;
    const run = () => {
      if (cancelled) return;
      positionTabSlider();
    };
    const track = tabsTrackRef.current;
    const alreadyReady = track?.hasAttribute('data-slider-ready');
    if (alreadyReady) {
      /* Tab switch: measure before paint so pill starts sliding under white label */
      run();
    } else {
      /* First mount: double rAF like prototype (layout settle) */
      requestAnimationFrame(() => {
        requestAnimationFrame(run);
      });
    }
    const onResize = () => {
      positionTabSlider();
      updateTabScroll();
    };
    window.addEventListener('resize', onResize);
    /* Scrolling only moves the arrows — the slider scrolls along with the chips. */
    track?.addEventListener('scroll', updateTabScroll, { passive: true });
    updateTabScroll();
    let ro;
    if (typeof ResizeObserver !== 'undefined' && track) {
      ro = new ResizeObserver(onResize);
      ro.observe(track);
    }
    return () => {
      cancelled = true;
      window.removeEventListener('resize', onResize);
      track?.removeEventListener('scroll', updateTabScroll);
      ro?.disconnect();
    };
  }, [positionTabSlider, updateTabScroll, tabProgress, loading, participant?._id, visibleTabs.length]);

  /* Bring the active chip into view when the tab changes (kept apart so a progress update doesn't snap the row back). */
  useEffect(() => {
    const track = tabsTrackRef.current;
    const btn = tabBtnRefs.current[activeTab];
    if (!track || !btn) return;
    track.scrollTo({ left: btn.offsetLeft - (track.clientWidth - btn.offsetWidth) / 2, behavior: 'smooth' });
  }, [activeTab, loading, participant?._id]);

  /** Reset cached form state when navigating to a different participant (same route, new :id). */
  useEffect(() => {
    latestFormDataRef.current = null;
    hasLoadedOnce.current = false;
    setAccreditationKey((k) => k + 1);
  }, [id]);

  /* Soft refresh when Accreditation opens — do NOT remount Form.io (that killed tab loads). */
  useEffect(() => {
    if (activeTab !== TABS[1]) return;
    fetchParticipantData(true);
  }, [activeTab, fetchParticipantData]);

  /* Called by AccreditationPhotoManager after a successful crop/save on the admin side. */
  const handlePhotoManagerUpdate = React.useCallback(() => {
    fetchParticipantData(true).then(() => {
      setAccreditationKey((k) => k + 1);
      setSubmissionVersion((v) => v + 1);
    });
  }, [fetchParticipantData]);

  /* Watch the tab content div for Formio's photo field and store it as a portal target. */
  useEffect(() => {
    if (activeTab !== TABS[1]) { setPhotoFieldPortalEl(null); return; }
    const container = tabContentRef.current;
    if (!container) return;
    const find = () => {
      // Thumbnail column only — portaled buttons sit under the image (layout from FormioOverrides.css).
      const img = container.querySelector('.formio-component-photo img');
      setPhotoFieldPortalEl(img?.parentElement || null);
    };
    find();
    const obs = new MutationObserver(find);
    obs.observe(container, { childList: true, subtree: true });
    return () => { obs.disconnect(); setPhotoFieldPortalEl(null); };
  }, [activeTab, accreditationKey]);

  /* --------------------- Helpers --------------------- */
  const replacePlaceholders = (templateBody, person) => {
    if (!templateBody) return '';
    const firstName = person?.name || '';
    const lastName = person?.lname || '';
    const fullName = `${firstName} ${lastName}`.trim();

    return templateBody
      .replace(/{{name}}/g, firstName)
      .replace(/{{lname}}/g, lastName)
      .replace(/{{fullname}}/g, fullName)
      .replace(/{{email}}/g, person?.email || '')
      .replace(/{{phone}}/g, person?.phone || person?.phone1 || person?.phone2 || '')
      .replace(/{{competition}}/g, person?.competition || '');
  };

  const safeParsePersonData = (rawData) => {
  if (!rawData) return {};
  
  let processed = rawData;

  // 1. Loop to handle double/triple stringification
  while (typeof processed === 'string') {
    try {
      const parsed = JSON.parse(processed);
      
      // 2. Check for the "Character Map" corruption
      // if parsed is an object and looks like {"0": "{", "1": "..."}
      if (parsed && typeof parsed === 'object' && parsed["0"] === "{") {
        processed = Object.values(parsed).join("");
      } else {
        processed = parsed;
      }
    } catch (e) {
      // If it's a string but not valid JSON (like a plain ID), return empty object
      return {};
    }
  }

  // 3. Final check: Ensure we return an object, never null or a string
  return (processed && typeof processed === 'object') ? processed : {};
};

  const initialSubmission = useMemo(() => {
  if (!participant) return { data: {} };

  const person = participant.person_id || {};
  const savedData = safeParsePersonData(participant.person_data);
  const groupId = participant.group_id;
  const groupIdVal =
    savedData.Group_id != null && savedData.Group_id !== ""
      ? String(savedData.Group_id)
      : groupId
        ? typeof groupId === "object"
          ? String(groupId._id || "")
          : String(groupId)
        : "";
  const groupFromHeader = groups.find((g) => String(g._id) === String(selectedGroup));
  const groupNameVal =
    groupFromHeader?.group_name != null
      ? String(groupFromHeader.group_name)
      : savedData.groupName != null && savedData.groupName !== ""
        ? String(savedData.groupName)
        : groupId?.group_name != null
          ? String(groupId.group_name)
          : "";

  const data = buildInitialFormData({
    savedPersonData: savedData,
    groupId: selectedGroup || groupIdVal,
    groupName: groupNameVal,
    ...assignmentNames,
    personRecord: person,
  });
  // Person record wins for core identity in admin edit (guards against cloned person_data).
  if (person._id) {
    if (person.name) data.givenFirstName = person.name;
    if (person.lname) data.familyLastName = person.lname;
    if (person.email) data.emailAddress = person.email;
    data.personId = String(person._id);
  }
  applyScoreboardNameFallback(data);
  if (data.photo != null) {
    data.photo = normalizeAccreditationPhotoForFormio(data.photo, API_URL);
  }
  // Sync the participant's assigned invitation type into the Form.io field
  if (selectedInvitationType) {
    data.invitationTypes = selectedInvitationType;
  }

  // Travel/Accommodation/Guest share one person_data.dataGrid array, but each
  // tab's Form.io dataGrid component renders every row in it regardless of
  // which tab the row belongs to. Guest rows are tagged with
  // participant_guest_id (attached server-side from the ParticipantGuest
  // collection); Travel/Accommodation rows never carry that tag. Scope each
  // tab's view to its own rows so e.g. the Guest tab doesn't also render the
  // Accommodation row as a blank guest entry.
  if (Array.isArray(data.dataGrid)) {
    const knownCaddies = data.dataGrid.knownCaddies;
    let filtered =
      activeTab === 'GUEST'
        ? data.dataGrid.filter((row) => row && row.participant_guest_id)
        : activeTab === 'TRAVEL' || activeTab === 'ACCOMMODATION'
          ? data.dataGrid.filter((row) => !row?.participant_guest_id)
          : data.dataGrid;
    if (knownCaddies !== undefined) filtered.knownCaddies = knownCaddies;
    if (activeTab === 'ACCOMMODATION') filtered = dropTrailingBlankAccommodationRows(filtered);
    if (activeTab === 'TRAVEL') filtered = dropTrailingBlankTravelRows(filtered);
    data.dataGrid = filtered;
  }

  // normalizeDataGridForFormioRender restores dataGrid rows into the Registration
  // tab's caddie-select shape — it only makes sense for Registration's own
  // caddie picker widget. Guest/Travel/Accommodation already got their own rows
  // scoped out of the shared dataGrid above; running the caddie restore on top
  // of that scoped (non-caddie) array overwrites it with caddie rows, which have
  // nothing to do with those tabs' schema and blank out their real data on screen.
  const isRegistrationTab = activeTab === TABS[0];
  try {
    const cloned = JSON.parse(JSON.stringify(data));
    return { data: isRegistrationTab ? normalizeDataGridForFormioRender(cloned) : cloned };
  } catch {
    return { data: isRegistrationTab ? normalizeDataGridForFormioRender({ ...data }) : { ...data } };
  }
}, [participant?._id, participant?.person_id, participant?.person_data, participant?.group_id, selectedGroup, assignmentNames, selectedInvitationType, groups, API_URL, activeTab]);

  /** Same source as Formio + UserFormView: submission.data.photo[0] */
  const accreditationPhotoUrls = useMemo(
    () =>
      activeTab === TABS[1]
        ? getAccreditationPhotoDisplayUrls(initialSubmission?.data, API_URL)
        : { currentPhotoUrl: null, originalPhotoUrl: null },
    [activeTab, initialSubmission, API_URL]
  );

  /** Live edits stay in latestFormDataRef (FormioTabRenderer) — do not setParticipant
   *  on every keystroke or parent re-renders remount/reset the form. */
  const handleFormChange = (tab, newData) => {
    const fields = newData?.data;
    if (!fields || typeof fields !== 'object') return;
    const existing =
      latestFormDataRef.current && typeof latestFormDataRef.current === 'object'
        ? latestFormDataRef.current
        : {};
    let next;
    try {
      next = {
        ...existing,
        ...JSON.parse(JSON.stringify(fields)),
      };
    } catch {
      next = { ...existing, ...fields };
    }
    latestFormDataRef.current = next;

    const segment = TAB_PROGRESS_KEY[tab];
    const schema = activeFormSchemaRef.current;
    if (segment && Array.isArray(schema) && schema.length) {
      const { filled, total } = calculateVisibleFieldProgress(
        next,
        schema,
        formioWebformRef.current
      );
      setTabProgress((prev) => ({
        ...prev,
        [segment]: { total, completed: filled },
      }));
    }
  };

  const clearValidationMessage = () => {
    setPageMessage((prev) => (prev?.validationItems?.length ? null : prev));
  };

  /** Same as user portal onError — block save, highlight required fields, scroll to first. */
  const handleValidationErrors = React.useCallback(() => {
    isSavingRef.current = false;
    setShowInlineErrors(true);
    if (pageMessageTimerRef.current) clearTimeout(pageMessageTimerRef.current);
    setPageMessage({
      type: 'error',
      text: 'Please complete the highlighted required fields, then try Save again.',
    });
    pageMessageTimerRef.current = setTimeout(() => setPageMessage(null), 4000);

    /* Re-mark after React paints show-errors class — re-render can wipe Form.io DOM marks. */
    const reapply = () => {
      const fi = formioWebformRef.current;
      if (fi) forceFormioShowValidationErrors(fi);
      const firstInvalid = tabContentRef.current?.querySelector(
        '.formio-component.has-error, .formio-component:has(.is-invalid), .form-control.is-invalid, .choices.is-invalid'
      );
      firstInvalid?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    };
    window.requestAnimationFrame(() => {
      reapply();
      window.setTimeout(reapply, 50);
    });
  }, []);

  /* Same as user portal / GuestEdit: validate on submit only — no eager empty-field red marks. */
  const participantFormOptions = useMemo(
    () => ({ noAlerts: true, highlightErrors: false }),
    []
  );

  /**
   * Switch tabs immediately so the next form always loads.
   * Quiet-save the leaving tab in the background (never block navigation).
   * Capture schema + data BEFORE switch — activeFormSchemaRef changes after remount.
   */
  const handleTabChange = (tab) => {
    if (tab === activeTab) return;

    clearValidationMessage();
    setShowInlineErrors(false);
    clearFormErrorsRef.current?.();

    const leavingTab = activeTab;
    const leavingSchema = activeFormSchemaRef.current;
    flushLiveFormDataRef.current?.();
    let snapshot = {};
    try {
      snapshot = cloneDeep(latestFormDataRef.current || {});
    } catch {
      snapshot = { ...(latestFormDataRef.current || {}) };
    }

    setActiveTab(tab);

    // Background quiet-save on every tab switch is disabled entirely.
    // Travel/Accommodation/Guest share one person_data.dataGrid row, and
    // multiple independent code paths that run during save (the client-side
    // merge, and separately preserveAndNormalizeCaddiesOnSave for any
    // participant with caddie fields — e.g. Registration tab saves for a
    // Caddie-group participant) can each rewrite that shared array from a
    // stale snapshot, clobbering fields owned by a different tab. Scoping
    // the disable to just the three sharing tabs was not enough, since the
    // caddie-sync path can fire from Registration too. Until the shared-row
    // model changes, every tab requires an explicit Save.
    if (false) {
      tabSwitchSaveInFlightRef.current = true;
      Promise.resolve(
        handleFormSave(snapshot, {
          viaTabSwitch: true,
          tabOverride: leavingTab,
          schemaOverride: leavingSchema,
        })
      )
        .catch((err) => {
          console.warn('Background tab save failed:', err);
        })
        .finally(() => {
          tabSwitchSaveInFlightRef.current = false;
        });
    }
  };

  /** Save & Continue should always advance — find the saved tab in the visible,
   * ordered tab list and switch to whatever comes after it. No-op on the last tab. */
  const goToNextTab = (fromTab) => {
    const idx = visibleTabs.indexOf(fromTab);
    if (idx === -1 || idx === visibleTabs.length - 1) return;
    handleTabChange(visibleTabs[idx + 1]);
  };

  const handleSubmit = async () => {
    if (activeTab === 'SEND EMAIL' || !forms[activeTab]) return;
    clearValidationMessage();
    /* Do NOT clear Form.io errors before validate — that wiped is-invalid marks. */
    isSavingRef.current = false;
    const submitFn = triggerFormSubmitRef.current;
    if (typeof submitFn === 'function') {
      await submitFn();
    }
  };


  const handlePhotoUpload = async (event) => {
  const file = event.target.files[0];
  if (!file) return;

  setPageMessage(null);

  const formData = new FormData();
  formData.append('file', file);

  const uploadUrl = getParticipantEditUploadUrl(API_URL);
  try {
    const uploadRes = await axios.post(uploadUrl, formData, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });

    const photoUrl = uploadRes.data?.url;
    if (!photoUrl) {
      setPageMessage({ type: "error", text: "Upload did not return a file URL. Please try again." });
      return;
    }
    const filename = typeof photoUrl === 'string' ? photoUrl.split('/').pop() : photoUrl;

    const currentData = safeParsePersonData(participant?.person_data);
    const profileAsFormioFile = [{ url: filename }];
    const updatedPersonData = {
      ...currentData,
      editProfilePhoto: profileAsFormioFile,
    };
    setProfilePhoto(filename);
    setParticipant(prev => ({
      ...prev,
      person_data: updatedPersonData
    }));

    if (fileInputRef.current) fileInputRef.current.value = '';

    setPageMessage({
      type: "success",
      text: "Photo added. Switch tab or use Submit to save.",
    });
  } catch (err) {
    console.error("Upload failed:", err);
    setPageMessage({ type: "error", text: err.response?.data?.message || "Image upload failed. Please try again." });
  }
};

  /**
   * Called by FormioTabRenderer onSubmit — Form.io validated (same flow as UserFormView).
   * @returns {Promise<boolean>}
   */
  const handleFormSave = async (formData, saveOptions = {}) => {
    if (!participant) return false;
    const viaTabSwitch = saveOptions.viaTabSwitch === true;
    /** Form.io may fire onSubmit once per panel; only the first explicit Submit runs the API. */
    if (isSavingRef.current) {
      return viaTabSwitch ? null : true;
    }
    isSavingRef.current = true;
    const tabAtSave = saveOptions.tabOverride || activeTabRef.current;

    if (!viaTabSwitch) {
      clearTimeout(pageMessageTimerRef.current);
      setPageMessage({ type: "success", text: `Saving ${tabAtSave}…` });
    }

    flushLiveFormDataRef.current?.();

    let effectiveFormData =
      formData &&
      typeof formData === "object" &&
      !Array.isArray(formData) &&
      Object.keys(formData).length > 0
        ? cloneDeep(formData)
        : cloneDeep(latestFormDataRef.current || {});

    const schemaForSave = saveOptions.schemaOverride || activeFormSchemaRef.current;
    const isRegistrationSave = tabAtSave === "REGISTRATION";
    const hasCaddie =
      isRegistrationSave &&
      (caddiesByIdRef.current.size > 0 ||
        formSchemaHasCaddieSelect(schemaForSave || []) ||
        hasRegistrationCaddieRef.current);

    const saved = safeParsePersonData(participant.person_data);
    const groupDoc = groups.find((g) => String(g._id) === String(selectedGroup));

    // Travel/Accommodation/Guest all share one dataGrid row. A background
    // tab-switch save carries whatever the leaving tab's Form.io instance had
    // captured for the WHOLE shared row (including other tabs' fields, which
    // may be stale/blank there) — merging that in wholesale would blank
    // fields this tab never touched. Scope each tab's row writes to its own
    // schema's keys, and merge into the existing row instead of replacing it.
    const dataGridOwnKeys = schemaForSave ? collectFormDataKeys(schemaForSave) : null;
    const mergeDataGridRowsClient = (existingRows, incomingRows) => {
      const existing = Array.isArray(existingRows)
        ? existingRows.map((row) => ({ ...row }))
        : [];
      const incoming = Array.isArray(incomingRows) ? incomingRows : [];
      // Travel/Accommodation tabs only ever see a guest-less subset of
      // dataGrid (filtered out above), so a Travel/Accommodation row's
      // position in `incoming` is its index WITHIN that subset — not its
      // index in the full `existing` array, which can also hold guest rows
      // at lower indices. Matching by raw array index merges the
      // Travel/Accommodation row straight into an unrelated guest's row
      // (confirmed live: a guest row ended up with checkInDate/checkOutDate
      // bolted onto it). Match non-guest incoming rows against the Nth
      // non-guest existing row instead.
      const nonGuestExistingIndices = existing.reduce((acc, row, idx) => {
        if (!row?.participant_guest_id) acc.push(idx);
        return acc;
      }, []);
      let nonGuestCursor = 0;
      incoming.forEach((incomingRow) => {
        if (!incomingRow || typeof incomingRow !== "object") return;
        const guestId = incomingRow.participant_guest_id;
        let idx;
        if (guestId) {
          idx = existing.findIndex(
            (row) => row && String(row.participant_guest_id) === String(guestId)
          );
        } else {
          idx = nonGuestExistingIndices[nonGuestCursor] ?? -1;
          nonGuestCursor += 1;
        }
        const scopedIncoming = {};
        Object.keys(incomingRow).forEach((k) => {
          if (!dataGridOwnKeys || dataGridOwnKeys.size === 0 || dataGridOwnKeys.has(k)) {
            scopedIncoming[k] = incomingRow[k];
          }
        });
        if (idx >= 0 && idx < existing.length) {
          existing[idx] = { ...existing[idx], ...scopedIncoming };
        } else {
          existing.push({ ...incomingRow });
        }
      });
      return existing;
    };

    let merged = mergeWith(
      {},
      saved,
      effectiveFormData,
      (objValue, src, key) =>
        Array.isArray(src)
          ? key === "dataGrid"
            ? mergeDataGridRowsClient(objValue, src)
            : cloneDeep(src)
          : undefined
    );
    merged = {
      ...merged,
      Group_id: selectedGroup || merged.Group_id || "",
      groupName: groupDoc?.group_name || merged.groupName || "",
      ...assignmentNames,
    };

    if (schemaForSave) {
      merged = filterPersonDataForFormSave(merged, schemaForSave, saved);
    }

    if (profilePhoto) {
      merged = { ...merged, editProfilePhoto: [{ url: profilePhoto }] };
    }

    try {
      if (tabAtSave === 'GUEST') {
        const guestRows =
          (Array.isArray(formData?.dataGrid) && formData.dataGrid) ||
          (formData?.guestDetails &&
            Array.isArray(formData.guestDetails.dataGrid) &&
            formData.guestDetails.dataGrid) ||
          (formData?.guest &&
            Array.isArray(formData.guest.dataGrid) &&
            formData.guest.dataGrid) ||
          [];
        await axiosAuth.put(`${API_URL}/ems/participants/${id}/guests`, guestRows);
        delete merged.guestDetails;
        delete merged.guest;
        if (personDataHasCaddieFields(saved)) {
          // dataGrid is the shared Guest/Travel/Accommodation row key.
          // copyCaddieFieldsFromSource also copies a caddie-shaped grid
          // from `saved` (IGFEMS-568) — if `saved.dataGrid` is itself
          // still in the old corrupted `{knownCaddies:[...]}` shape from
          // a prior bad save, that would clobber the guest row this save
          // just merged in. Keep presentCaddies/additionalCaddies from
          // copyCaddieFieldsFromSource, but never let it overwrite the
          // dataGrid this save already computed.
          const dataGridFromThisSave = merged.dataGrid;
          copyCaddieFieldsFromSource(merged, saved);
          merged.dataGrid = dataGridFromThisSave;
        } else {
          delete merged.dataGrid;
        }
      }

      if (hasCaddie || personDataHasCaddieFields(saved)) {
        merged = preserveAndNormalizeCaddiesOnSave(merged, saved, {
          caddiesById: caddiesByIdRef.current,
          formio: formioWebformRef.current,
          isRegistrationTab: isRegistrationSave,
          liveSnapshot: latestFormDataRef.current,
          tabLabel: tabAtSave,
        });
      }

      const personForIdentity = participant?.person_id || {};
      preserveParticipantIdentityFromPerson(merged, personForIdentity);

      // If the Form.io invitation type dropdown was changed, sync it back to the header field
      const formInvitationType = merged?.invitationTypes || effectiveFormData?.invitationTypes || '';
      const effectiveInvitationType = formInvitationType || selectedInvitationType || null;
      if (formInvitationType && formInvitationType !== selectedInvitationType) {
        setSelectedInvitationType(formInvitationType);
      }

      await axios.put(`${API_URL}/ems/participants/${id}`, {
        person_data: merged,
        registration_status: registrationStatus,
        approval_status: approvalStatus,
        group_id: selectedGroup || null,
        invitation_type_id: effectiveInvitationType,
        role_id: selectedEmsRole || null,
        accreditation_category_id: selectedAccreditationCategory || null,
        progress_breakdown: tabProgress,
        admin_edit: true,
      });

      setParticipant((prev) =>
        prev
          ? {
              ...prev,
              person_data: merged,
              progress_breakdown: tabProgress,
              registration_status: registrationStatus,
              approval_status: approvalStatus,
              group_id: groupDoc || selectedGroup || prev.group_id,
              invitation_type_id: invitationTypes.find((t) => String(t._id) === String(effectiveInvitationType)) || effectiveInvitationType || null,
              role_id: emsRoles.find((r) => String(r._id) === String(selectedEmsRole)) || selectedEmsRole || null,
              accreditation_category_id: accreditationCategories.find(
                (c) => String(c._id) === String(selectedAccreditationCategory)
              ) || selectedAccreditationCategory || null,
            }
          : prev
      );
      latestFormDataRef.current =
        hasCaddie || personDataHasCaddieFields(merged)
          ? normalizeDataGridForFormioRender(JSON.parse(JSON.stringify(merged)))
          : merged;
      clearFormErrorsRef.current?.();
      setShowInlineErrors(false);
      await fetchParticipantData(true);
      if (!viaTabSwitch) {
        setSubmissionVersion((v) => v + 1);
        clearTimeout(pageMessageTimerRef.current);
        setPageMessage({
          type: "success",
          text: `${tabAtSave} saved successfully.`,
        });
        pageMessageTimerRef.current = setTimeout(() => setPageMessage(null), 3000);
        goToNextTab(tabAtSave);
      }
      return true;
    } catch (err) {
      console.error("Save failed:", err);
      if (!viaTabSwitch) {
        const apiMsg = err.response?.data?.message;
        setPageMessage({
          type: "error",
          text: apiMsg || "Failed to save. Please try again.",
        });
      }
      return false;
    } finally {
      isSavingRef.current = false;
    }
  };

  const handleSaveAssignment = async (overrides = {}) => {
    if (!id || assignmentSaving) return;
    setAssignmentSaveError('');
    setAssignmentSaving(true);
    // The selected* state still holds the old value here — the new one only arrives via overrides.
    const nextRegistrationStatus = overrides.registrationStatus ?? registrationStatus;
    const nextApprovalStatus = overrides.approvalStatus ?? approvalStatus;
    const nextGroup = (overrides.groupId ?? selectedGroup) || null;
    const nextInvitationType = (overrides.invitationTypeId ?? selectedInvitationType) || null;
    const nextRole = (overrides.roleId ?? selectedEmsRole) || null;
    const nextAccreditationCategory =
      (overrides.accreditationCategoryId ?? selectedAccreditationCategory) || null;
    try {
      await axios.put(`${API_URL}/ems/participants/${id}`, {
        registration_status: nextRegistrationStatus,
        approval_status: nextApprovalStatus,
        group_id: nextGroup,
        invitation_type_id: nextInvitationType,
        role_id: nextRole,
        accreditation_category_id: nextAccreditationCategory,
        admin_edit: true,
      });

      setParticipant((prev) =>
        prev
          ? {
              ...prev,
              registration_status: nextRegistrationStatus,
              approval_status: nextApprovalStatus,
              group_id: groups.find((g) => String(g._id) === String(nextGroup)) || nextGroup,
              invitation_type_id:
                invitationTypes.find((t) => String(t._id) === String(nextInvitationType)) ||
                nextInvitationType,
              role_id: emsRoles.find((r) => String(r._id) === String(nextRole)) || nextRole,
              accreditation_category_id:
                accreditationCategories.find(
                  (c) => String(c._id) === String(nextAccreditationCategory)
                ) || nextAccreditationCategory,
            }
          : prev
      );
      clearTimeout(pageMessageTimerRef.current);
      setPageMessage({ type: 'success', text: 'Assignment saved.' });
      pageMessageTimerRef.current = setTimeout(() => setPageMessage(null), 2500);
    } catch (err) {
      console.error('Assignment save failed:', err);
      setAssignmentSaveError(
        err?.response?.data?.message || 'Failed to save assignment. Please try again.'
      );
    } finally {
      setAssignmentSaving(false);
    }
  };

  const handleSaveMissionDates = async ({ mission_start_date, mission_end_date }) => {
    if (!id) return 'Participant is still loading.';
    try {
      await axios.put(`${API_URL}/ems/participants/${id}`, {
        mission_start_date,
        mission_end_date,
        admin_edit: true,
      });
      const cleared = mission_start_date == null && mission_end_date == null;
      setParticipant((prev) =>
        prev
          ? {
              ...prev,
              admin_plan: {
                ...(prev.admin_plan || {}),
                mission_start_date: cleared ? null : laDateKeyToUTC(mission_start_date).toISOString(),
                mission_end_date: cleared ? null : laDateKeyToUTC(mission_end_date).toISOString(),
              },
            }
          : prev
      );
      clearTimeout(pageMessageTimerRef.current);
      setPageMessage({
        type: 'success',
        text: cleared ? 'Mission dates cleared.' : 'Mission dates saved.',
      });
      pageMessageTimerRef.current = setTimeout(() => setPageMessage(null), 2500);
      return true;
    } catch (err) {
      console.error('Mission date save failed:', err);
      return err?.response?.data?.message || 'Failed to save mission dates. Please try again.';
    }
  };

  const refId = (value) =>
    value && typeof value === 'object' ? String(value._id || '') : value ? String(value) : '';

  const resetAssignmentDraftFromParticipant = () => {
    const p = participant;
    if (!p) return;
    setRegistrationStatus(p.registration_status || '');
    setApprovalStatus(p.approval_status || 'Pending');
    setSelectedGroup(refId(p.group_id));
    setSelectedInvitationType(refId(p.invitation_type_id));
    setSelectedEmsRole(refId(p.role_id));
    setSelectedAccreditationCategory(refId(p.accreditation_category_id));
    setAssignmentSaveError('');
  };

  const handleCloseDetails = () => {
    resetAssignmentDraftFromParticipant();
    setDetailsOpen(false);
  };

  const handleSendEmail = async (emailBody, subjectOverride) => {
    if (sendingEmail) return;
    if (!selectedTemplateId) {
      setPageMessage({ type: "error", text: "Select an email template first." });
      return;
    }
    const personData = participant?.person_id;
    if (!personData?._id) {
      setPageMessage({ type: "error", text: "Person record missing for this participant." });
      return;
    }
    if (!personData?.email) {
      setPageMessage({ type: "error", text: "This person has no email on file." });
      return;
    }

    const chosenTemplate = templates.find((t) => t._id === selectedTemplateId);
    if (!chosenTemplate) {
      setPageMessage({ type: "error", text: "That template is no longer available. Refresh the page and try again." });
      return;
    }

    // Same path as Communication → Send Emails: POST /ems/send-emails
    const rawForSend =
      emailBody ||
      chosenTemplate.body ||
      chosenTemplate.templateBody ||
      chosenTemplate.content ||
      '';
    const bodyToSend = replacePlaceholders(rawForSend, personData);
    // Same API as Communication → Send Emails. Server authenticates with .env SMTP_*.
    const emailPayload = {
      from_name: fromName,
      emails: [{
        personId: personData._id,
        subject: subjectOverride || chosenTemplate.subject || templateSubject || 'Notification',
        body: bodyToSend,
        attachments: templateFiles,
      }],
    };

    setSendingEmail(true);
    try {
      // axiosAuth attaches Bearer; withCredentials for cookie sessions
      const sendRes = await axiosAuth.post(`${API_URL}/ems/send-emails`, emailPayload);
      const results = Array.isArray(sendRes.data?.results) ? sendRes.data.results : [];
      const failed = results.find((r) => r.status === 'error');
      if (failed || results.length === 0) {
        setPageMessage({
          type: 'error',
          text: failed?.message || sendRes.data?.message || 'Failed to send email.',
        });
        return;
      }

      // Server already marks Added → Pending; this keeps UI in sync for this participant
      try {
        const pendingRes = await axiosAuth.put(
          `${API_URL}/ems/participants/${id}/mark-pending`
        );
        const pendingStatus = pendingRes.data?.registration_status || 'Pending';
        setRegistrationStatus(pendingStatus);
        setParticipant((prev) =>
          prev ? { ...prev, registration_status: pendingStatus } : prev
        );
      } catch (pendingErr) {
        console.warn('mark-pending after send failed', pendingErr);
        // Optimistic UI if API mark already ran inside send-emails
        setRegistrationStatus((prev) => (prev === 'Added' ? 'Pending' : prev));
        setParticipant((prev) =>
          prev && prev.registration_status === 'Added'
            ? { ...prev, registration_status: 'Pending' }
            : prev
        );
      }

      setSendResult({ sent: 1, failed: [] });
    } catch (err) {
      console.error('Send email failed', err);
      const results = err.response?.data?.results;
      const failedMsg = Array.isArray(results)
        ? results.find((r) => r.status === 'error')?.message
        : null;
      const apiMsg = failedMsg || err.response?.data?.message || err.response?.data?.error;
      setPageMessage({
        type: "error",
        text: apiMsg || "Failed to send email. Please try again.",
      });
    } finally {
      setSendingEmail(false);
    }
  };

  useEffect(() => {
    setPageMessage((prev) => {
      if (!prev?.validationItems?.length) return prev;
      return prev.errorTab === activeTab ? prev : null;
    });
  }, [activeTab]);

  const personIdForInvite = normalizePersonId(
    participant?.person_id?._id || participant?.person_id
  );

  useEffect(() => {
    if (!selectedTemplateId || !personIdForInvite) {
      if (!selectedTemplateId) {
        setTemplateName('');
        setTemplateSubject('');
        setTemplateFiles([]);
        setEditorHtml('');
      }
      return;
    }
    const selectedTemplate = templates.find((t) => t._id === selectedTemplateId);
    if (!selectedTemplate) return;
    const person = participant?.person_id;
    setTemplateName(selectedTemplate.name || '');
    setTemplateSubject(selectedTemplate.subject || '');
    const rawBody = selectedTemplate.body ?? selectedTemplate.templateBody ?? selectedTemplate.content ?? '';
    setEditorHtml(replacePlaceholders(rawBody, person));
    setTemplateFiles(Array.isArray(selectedTemplate.attachments) ? selectedTemplate.attachments : []);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- person fields read when personId is ready
  }, [selectedTemplateId, personIdForInvite, templates.length]);

  // Rendered by calling it (not as <SendEmailTabContent />): a component defined inside this one gets a
  // new identity every render and would remount, reloading ParticipantEmailHistory on each state change.
  const SendEmailTabContent = () => {
    const personIdForHistory = participant?.person_id?._id;
    if (!templates.length) {
      return (
        <div className="p-3">
          <ParticipantEmailHistory personId={personIdForHistory} />
          <div className="text-muted">No templates available.</div>
        </div>
      );
    }
    return (
      <div className="p-3">
        <ParticipantEmailHistory personId={personIdForHistory} />
        <div className="mb-3">
          <label className="form-label fw-bold">Select Template</label>
          <select className="form-select pe-template-select" value={selectedTemplateId} onChange={(e) => setSelectedTemplateId(e.target.value)}>
            <option value="">-- Select Template --</option>
            {templates.map((t) => (
              <option key={t._id} value={t._id}>{t.name || t.subject || 'Untitled Template'}</option>
            ))}
          </select>
        </div>
        {selectedTemplateId && (
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleSendEmail(editorHtml, templateSubject);
            }}
            className="border rounded p-3"
            style={{ background: '#fff' }}
          >
            <input
              type="text"
              className="form-control border-0 border-bottom mb-3"
              placeholder="Template Name"
              value={templateName}
              onChange={(e) => setTemplateName(e.target.value)}
              required
            />
            <ReactQuill
              ref={quillRef}
              value={editorHtml}
              onChange={setEditorHtml}
              style={{ minHeight: '200px', marginBottom: '8px' }}
            />
            <div className="d-flex justify-content-end align-items-center gap-2 mt-3 pe-send-email-actions">
              <button
                type="submit"
                className="btn pe-send-email-submit"
                disabled={sendingEmail}
              >
                {sendingEmail ? (
                  <>
                    <Spinner animation="border" size="sm" role="presentation" className="me-2" />
                    Sending...
                  </>
                ) : (
                  'Send Email'
                )}
              </button>
              <button
                type="button"
                className="btn participant-edit-cancel-btn"
                disabled={sendingEmail}
                onClick={() => setSelectedTemplateId('')}
              >
                Cancel
              </button>
            </div>
          </form>
        )}
        <SendResultModal result={sendResult} onClose={() => setSendResult(null)} />
      </div>
    );
  };

  if (loading) {
    return (
      <GoldLoader label="Loading..." variant="page" />
    );
  }
  if (!participant) return <div className="p-4 text-center text-danger">Participant not found</div>;

  const person = participant?.person_id || {};
  const activeFormId = forms[activeTab];
  // schemaCacheTick: re-read cache after prefetch completes
  const cachedFormEntry = schemaCacheTick >= 0
    ? schemaCacheRef.current[activeFormId]
    : null;

  const bannerMessage =
    pageMessage && !pageMessage.validationItems?.length ? pageMessage : null;

  const displayName = `${person?.name || ''} ${person?.lname || ''}`.trim() || 'Participant';
  const headerAvatarSrc =
    resolvePersonImageUrl(profilePhoto || person?.image, API_URL) ||
    profilePhoto ||
    '';
  const missionDates = resolveMissionDates(participant);
  const { arrivalPeriods, stayPeriods } = resolveParticipantDatePeriods(participant);

  return (
    <div
      ref={pageRef}
      className={`participant-edit-page user-layout user-form-prototype${
        showInlineErrors ? ' user-form--show-errors' : ''
      }${detailsOpen ? ' participant-edit-page--details-open' : ''}`}
    >
      {bannerMessage && (
        <div
          className={`participant-edit-toast alert ${
            bannerMessage.type === 'success' ? 'alert-info' : 'alert-danger'
          } position-fixed top-0 start-50 translate-middle-x mt-3 shadow`}
          role="status"
          aria-live="polite"
        >
          {bannerMessage.text}
        </div>
      )}

      <div className="participant-details-main-pane">
      <header className="user-form-context-row participant-edit-header">
        <section className="user-form-profile" aria-label="Participant summary">
          <div className="user-form-profile__identity participant-edit-identity-row">
            <div className="user-form-profile__avatar participant-edit-header__avatar">
              {headerAvatarSrc ? (
                <img src={headerAvatarSrc} alt="" />
              ) : (
                <FontAwesomeIcon icon={faUser} />
              )}
              <button
                type="button"
                className="user-form-profile__edit"
                aria-label="Upload profile photo"
                onClick={() => fileInputRef.current?.click()}
              >
                <FontAwesomeIcon icon={faPen} />
              </button>
              <input
                type="file"
                accept={ACCREDITATION_ALLOWED_FILE_TYPES}
                hidden
                ref={fileInputRef}
                onChange={handlePhotoUpload}
              />
            </div>
            <div>
              <h1>{displayName}</h1>
            </div>
            {!detailsOpen && (
              <button
                type="button"
                className="participant-details-toggle"
                onClick={() => {
                  setAssignmentSaveError('');
                  setDetailsOpen(true);
                }}
              >
                Summary
              </button>
            )}
          </div>
        </section>
      </header>

      <nav className="participant-edit-tabs" aria-label="Participant form sections">
        <div className="participant-edit-tabs__wrap">
          {tabScroll.left && (
            <button
              type="button"
              className="participant-edit-tabs__arrow participant-edit-tabs__arrow--left"
              aria-label="Scroll tabs left"
              tabIndex={-1}
              onClick={() => scrollTabs(-1)}
            >
              <FontAwesomeIcon icon={faChevronLeft} />
            </button>
          )}
          <div className="participant-edit-tabs__track" ref={tabsTrackRef}>
            <div
              ref={tabSliderRef}
              className="participant-edit-tabs__slider"
              aria-hidden="true"
            />
            {visibleTabs.map((tab) => {
              const segment = TAB_PROGRESS_KEY[tab];
              const realPct = segment
                ? tabPercentFromProgress(tabProgress?.[segment])
                : null;
              const displayPct = segment != null ? (realPct == null ? 0 : realPct) : null;
              const isComplete = realPct === 100;
              return (
              <button
                key={tab}
                type="button"
                ref={(el) => {
                  if (el) tabBtnRefs.current[tab] = el;
                  else delete tabBtnRefs.current[tab];
                }}
                className={`participant-edit-tab${activeTab === tab ? ' participant-edit-tab--active' : ''}${
                  isComplete ? ' participant-edit-tab--complete' : ''
                }`}
                onClick={() => handleTabChange(tab)}
              >
                <span className="participant-edit-tab__label">{tabDisplayLabel(tab)}</span>
                {displayPct != null && (
                  <span
                    className="participant-edit-tab__pct"
                    aria-label={`${displayPct} percent complete`}
                  >
                    {displayPct}%
                  </span>
                )}
              </button>
              );
            })}
          </div>
          {tabScroll.right && (
            <button
              type="button"
              className="participant-edit-tabs__arrow participant-edit-tabs__arrow--right"
              aria-label="Scroll tabs right"
              tabIndex={-1}
              onClick={() => scrollTabs(1)}
            >
              <FontAwesomeIcon icon={faChevronRight} />
            </button>
          )}
        </div>
        </nav>

        <div className="participant-edit-scroll" ref={scrollContainerRef}>
        <div
          key={activeTab}
          className={`tab-content participant-form-body-enter ${participantFormTabClass(activeTab)}${
            showInlineErrors ? ' participant-form-tab--show-errors' : ''
          }`}
          ref={tabContentRef}
        >
          {showInlineErrors && activeFormId && activeTab !== 'SEND EMAIL' && activeTab !== 'BOOKING' && (
            <p className="user-form-inline-hint" role="alert">
              Please complete the highlighted required fields below.
            </p>
          )}
          {activeTab === 'SEND EMAIL' ? (
            SendEmailTabContent()
          ) : activeTab === 'BOOKING' ? (
            <ParticipantBookingTab participant={participant} onNavigateToTab={handleTabChange} />
          ) : activeFormId ? (
            <>
            {activeTab === TABS[1] && (
              <AccreditationPhotoManager
                ref={photoManagerRef}
                headless
                participantId={participant?._id}
                currentPhotoUrl={accreditationPhotoUrls.currentPhotoUrl}
                originalPhotoUrl={accreditationPhotoUrls.originalPhotoUrl}
                onPhotoUpdated={handlePhotoManagerUpdate}
              />
            )}
            {/* Portal: buttons below the photo */}
            {photoFieldPortalEl && ReactDOM.createPortal(
              <div className="d-flex gap-2" style={{marginBottom: '10px', marginLeft: '45px'}}>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-secondary"
                  onClick={() => photoManagerRef.current?.openView()}
                >
                  View
                </button>
                <button
                  type="button"
                  className="btn btn-sm"
                  style={{ background: '#D9A128', color: '#fff', border: 'none' }}
                  onClick={() => photoManagerRef.current?.openCrop()}
                >
                  Edit / Crop
                </button>
              </div>,
              photoFieldPortalEl
            )}
            <FormioTabRenderer
              key={`${participant?._id || 'loading'}-${activeFormId}-v${submissionVersion}${
                activeTab === TABS[1] ? `-acc${accreditationKey}` : ''
              }`}
              formId={activeFormId}
              participantId={participant?._id}
              submission={initialSubmission}
              submissionVersion={submissionVersion}
              initialSchema={cachedFormEntry?.schema || null}
              initialFormName={cachedFormEntry?.form_name || ''}
              onDataChange={(data) => handleFormChange(activeTab, data)}
              API_URL={API_URL}
              liveDataRef={latestFormDataRef}
              flushLiveFormDataRef={flushLiveFormDataRef}
              formioWebformRef={formioWebformRef}
              getLiveWebformRef={getLiveWebformRef}
              triggerSaveRef={triggerSaveRef}
              triggerFormSubmitRef={triggerFormSubmitRef}
              clearFormErrorsRef={clearFormErrorsRef}
              onFormSave={handleFormSave}
              onValidationErrors={handleValidationErrors}
              showInlineErrors={showInlineErrors}
              addCaddie={addCaddieConfig}
              formOptions={participantFormOptions}
              showProgressBadges
              onProgressStats={({ filled, total }) => {
                const segment = TAB_PROGRESS_KEY[activeTab];
                if (!segment) return;
                setTabProgress((prev) => {
                  const cur = prev[segment];
                  if (
                    Number(cur?.total) === total &&
                    Number(cur?.completed) === filled
                  ) {
                    return prev;
                  }
                  return {
                    ...prev,
                    [segment]: {
                      total,
                      completed: filled,
                      display: `${filled}/${total}`,
                    },
                  };
                });
              }}
              sharedCaddiesByIdRef={caddiesByIdRef}
              onFormSchemaLoad={(components) => {
                activeFormSchemaRef.current = components;
                hasRegistrationCaddieRef.current = formSchemaHasCaddieSelect(
                  components || []
                );
                const segment = TAB_PROGRESS_KEY[activeTab];
                const data =
                  latestFormDataRef.current ||
                  safeParsePersonData(participant?.person_data);
                if (segment && Array.isArray(components) && components.length) {
                  const { filled, total } = calculateVisibleFieldProgress(
                    data,
                    components,
                    formioWebformRef.current
                  );
                  setTabProgress((prev) => ({
                    ...prev,
                    [segment]: {
                      total,
                      completed: filled,
                      display: `${filled}/${total}`,
                    },
                  }));
                }
              }}
            />
            {activeTab === 'ACCOMMODATION' && <AccommodationSection bookingGroups={bookingGroups} />}
            </>
          ) : activeTab === 'PERSONAL SETTINGS' && personalSettingsResolving ? (
            <GoldLoader label="Loading..." variant="panel" />
          ) : (
            <div className="p-3 text-muted">Form not found for this tab.</div>
          )}
        </div>

        {activeTab !== 'SEND EMAIL' && (
          <div className="mt-4 d-flex gap-2 justify-content-end participant-edit-footer-actions">
            {activeFormId && (
              <button
                type="button"
                className="btn participant-edit-submit-btn"
                onClick={handleSubmit}
              >
                Save & Continue
              </button>
            )}
          </div>
        )}
        </div>
      </div>

      <ParticipantDetailsSidebar
        open={detailsOpen}
        onClose={handleCloseDetails}
        mode="admin"
        registrationStatus={registrationStatus}
        approvalStatus={approvalStatus}
        groupId={selectedGroup}
        roleId={selectedEmsRole}
        invitationTypeId={selectedInvitationType}
        accreditationCategoryId={selectedAccreditationCategory}
        groups={groups}
        roles={emsRoles}
        invitationTypes={invitationTypes}
        accreditationCategories={accreditationCategories}
        formatAccreditationOption={formatAccreditationCategoryLabel}
        onRegistrationChange={(val) => { setRegistrationStatus(val); handleSaveAssignment({ registrationStatus: val }); }}
        onApprovalChange={(val)     => { setApprovalStatus(val);     handleSaveAssignment({ approvalStatus: val }); }}
        onGroupChange={(val)        => { setSelectedGroup(val);       handleSaveAssignment({ groupId: val }); }}
        onRoleChange={(val)         => { setSelectedEmsRole(val);     handleSaveAssignment({ roleId: val }); }}
        onInvitationChange={(val)   => { setSelectedInvitationType(val); handleSaveAssignment({ invitationTypeId: val }); }}
        onAccreditationChange={(val)=> { setSelectedAccreditationCategory(val); handleSaveAssignment({ accreditationCategoryId: val }); }}
        missionFrom={missionDates.from}
        missionTo={missionDates.to}
        onMissionDatesSave={handleSaveMissionDates}
        arrivalPeriods={arrivalPeriods}
        stayPeriods={stayPeriods}
        saving={assignmentSaving}
        saveError={assignmentSaveError}
      />
    </div>
  );
};

export default ParticipantEdit;